// Mini-CPU und Prüfer -- Gegenstück zu codelab/cpu.py. Beide müssen tests/fixtures/programm.json
// bestehen (siehe model.test.ts).
//
// Speicher: 16 Zellen zu 8 Bit, Programm und Daten gemeinsam (Von Neumann). Befehl = 4 Bit
// Befehlscode + 4 Bit Adresse. Eine leere Zelle (00000000) ist HALT.

export const ZELLEN = 16;
export const BITS = 8;
export const MAX_SCHRITTE = 100;

export type Befehl = "HALT" | "LOAD" | "STORE" | "INC" | "SHL";

/** Befehlscode → Kürzel und ob die rechten 4 Bit eine Adresse sind. */
export const BEFEHLE: Record<string, { name: Befehl; adresse: boolean }> = {
  "0000": { name: "HALT", adresse: false },
  "0001": { name: "LOAD", adresse: true },
  "0010": { name: "STORE", adresse: true },
  "0011": { name: "INC", adresse: false },
  "0100": { name: "SHL", adresse: false },
};
export const CODES = Object.fromEntries(Object.entries(BEFEHLE).map(([c, b]) => [b.name, c])) as Record<Befehl, string>;

export interface Datum { zelle: number; wert: number; name: string }
export interface Task {
  id: string; titel: string; text: string; hinweise: string[]; block: string | null;
  typ: "maschinencode" | "assembler";
  vorlage?: string;           // nur assembler: Quelltext zu Beginn
  befehle: Befehl[];          // Palette ohne HALT
  daten: Datum[];
  ziel: { zelle: number; wert: number }[];
  par: number;
}

export interface Problem { code: string; ref: string | null; message: string }
export interface Zustand { speicher: string[]; bz: number; register: string; halt: boolean }
interface EvBase { bz: number; befehl: string }
export type Ereignis = EvBase & (
  | { typ: "halt" | "unbekannt" }
  | { typ: "load" | "store"; adresse: string; wert: string }
  | { typ: "inc" | "shl"; vorher: string; wert: string; ueberlauf: boolean });

export type Loesung = { speicher: string[] } | { quelltext: string };
export interface Result {
  solved: boolean;
  problems: Problem[];
  zustand: Zustand | null;
  ereignisse: Ereignis[];
  schritte: number;
  par_erreicht: boolean;
}

export const adr = (i: number) => i.toString(2).padStart(4, "0");
export const wort = (n: number) => (((n % 256) + 256) % 256).toString(2).padStart(BITS, "0");
export const LEER = wort(0);
const wortOk = (s: unknown): s is string => typeof s === "string" && s.length === BITS && /^[01]*$/.test(s);

export const anfang = (speicher: string[]): Zustand => ({ speicher: [...speicher], bz: 0, register: LEER, halt: false });

/** Speicher mit den Daten der Aufgabe, alle anderen Zellen leer (= HALT). */
export function speicherStart(task: Task): string[] {
  const sp = Array<string>(ZELLEN).fill(LEER);
  for (const d of task.daten) sp[d.zelle] = wort(d.wert);
  return sp;
}

/** Wie liest die CPU dieses Bitmuster als Befehl? z. B. "LOAD 14", "INC", null = unbekannt */
export function deute(w: string): string | null {
  const b = BEFEHLE[w.slice(0, 4)];
  if (!b) return null;
  return b.adresse ? `${b.name} ${parseInt(w.slice(4), 2)}` : b.name;
}

/** Ein ganzer Befehl: holen, entschlüsseln, ausführen. */
export function schritt(zustand: Zustand): [Zustand, Ereignis] {
  const sp = [...zustand.speicher], { bz, register: reg } = zustand;
  const befehl = sp[bz];
  const code = befehl.slice(0, 4), a = parseInt(befehl.slice(4), 2);
  const neu: Zustand = { speicher: sp, bz: (bz + 1) % ZELLEN, register: reg, halt: false };
  const b = BEFEHLE[code];
  if (!b) return [{ ...neu, halt: true, bz }, { bz, befehl, typ: "unbekannt" }];
  switch (b.name) {
    case "HALT": return [{ ...neu, halt: true, bz }, { bz, befehl, typ: "halt" }];
    case "LOAD":
      neu.register = sp[a];
      return [neu, { bz, befehl, typ: "load", adresse: adr(a), wert: sp[a] }];
    case "STORE":
      sp[a] = reg;
      return [neu, { bz, befehl, typ: "store", adresse: adr(a), wert: reg }];
    default: {
      const wert = b.name === "INC" ? parseInt(reg, 2) + 1 : parseInt(reg, 2) * 2;
      neu.register = wort(wert);
      return [neu, { bz, befehl, typ: b.name === "INC" ? "inc" : "shl", vorher: reg, wert: neu.register, ueberlauf: wert >= 256 }];
    }
  }
}

/** Läuft bis HALT, unbekanntem Befehl oder MAX_SCHRITTE. */
export function ausfuehren(zustand: Zustand): [Zustand, Ereignis[]] {
  const ev: Ereignis[] = [];
  while (!zustand.halt && ev.length < MAX_SCHRITTE) {
    const [z, e] = schritt(zustand);
    zustand = z;
    ev.push(e);
  }
  return [zustand, ev];
}

const problem = (code: string, ref: string | null, message: string): Problem => ({ code, ref, message });
const zahl = (w: string) => `${w} (${parseInt(w, 2)})`;

/** Zelle enthält einen Baustein aus der Palette (Befehle ohne Adresse mit Adressteil 0000). */
export function bausteinOk(task: Task, w: string): boolean {
  const b = BEFEHLE[w.slice(0, 4)];
  if (!b || (b.name !== "HALT" && !task.befehle.includes(b.name))) return false;
  return b.adresse || w.slice(4) === "0000";
}

/** Prüft, ob das Programm gültig ist, lässt es laufen und vergleicht den Endzustand. */
export function checkProgramm(task: Task, loesung: { speicher: string[] }): Result {
  const sp = loesung?.speicher;
  const result: Result = { solved: false, problems: [], zustand: null, ereignisse: [], schritte: 0, par_erreicht: false };
  const problems = result.problems;
  if (!Array.isArray(sp) || sp.length !== ZELLEN || !sp.every(wortOk)) {
    problems.push(problem("ungueltig", null, "Ungültiger Speicherinhalt"));
    return result;
  }
  const start = speicherStart(task);
  const datenzellen = new Set(task.daten.map(d => d.zelle));
  sp.forEach((w, i) => {
    if (datenzellen.has(i) && w !== start[i]) problems.push(problem("ungueltig", adr(i), `Die Datenzelle ${i} wurde verändert.`));
    else if (!datenzellen.has(i) && !bausteinOk(task, w)) problems.push(problem("ungueltig", adr(i), `Zelle ${i} enthält keinen erlaubten Baustein.`));
  });
  if (problems.length) return result;
  if (sp.every((w, i) => datenzellen.has(i) || w === LEER)) {
    problems.push(problem("kein_programm", null, "Noch kein Befehl im Speicher."));
    return result;
  }

  const [zustand, ereignisse] = ausfuehren(anfang(sp));
  Object.assign(result, { zustand, ereignisse, schritte: ereignisse.length });

  const letzter = ereignisse[ereignisse.length - 1];
  if (!zustand.halt) {
    problems.push(problem("endlos", null, `Das Programm hält nach ${MAX_SCHRITTE} Befehlen immer noch nicht an.`));
  } else if (letzter.typ === "unbekannt") {
    problems.push(problem("unbekannt", adr(letzter.bz), `In Zelle ${letzter.bz} steht ${letzter.befehl} -- den Befehlscode `
      + `${letzter.befehl.slice(0, 4)} kennt die CPU nicht, sie bricht ab.`));
  }
  const namen = new Map(task.daten.map(d => [d.zelle, d.name ? ` (${d.name})` : ""]));
  const alsBefehl = ereignisse.find(e => datenzellen.has(e.bz));
  if (alsBefehl) {
    problems.push(problem("daten_als_befehl", adr(alsBefehl.bz),
      `Die CPU hat die Datenzelle ${alsBefehl.bz}${namen.get(alsBefehl.bz)} als Befehl ausgeführt -- fehlt ein Befehl zum Anhalten (0000 0000)?`));
  }

  const zielZellen = new Set<number>();
  for (const z of task.ziel) {
    zielZellen.add(z.zelle);
    const ist = zustand.speicher[z.zelle], soll = wort(z.wert);
    if (ist !== soll) problems.push(problem("ziel_zelle", adr(z.zelle), `In Zelle ${z.zelle} steht ${zahl(ist)}, soll ${zahl(soll)}.`));
  }
  for (const d of task.daten) {
    const i = d.zelle;
    if (!zielZellen.has(i) && zustand.speicher[i] !== start[i]) {
      problems.push(problem("nebenwirkung", adr(i), `Die Datenzelle ${i}${namen.get(i)} wurde versehentlich überschrieben `
        + `(${zahl(start[i])} → ${zahl(zustand.speicher[i])}).`));
    }
  }

  result.solved = problems.length === 0;
  result.par_erreicht = result.solved && result.schritte <= task.par;
  return result;
}

// ---------------------------------------------------------------- Assembler (Gegenstück zu codelab/assembler.py)

export const MAX_QUELLTEXT = 4000;
export interface Zuordnung { zeile: number; zelle: number; wort: string }
export interface AsmFehler { zeile: number; message: string }
export interface Assembliert { speicher: string[]; zuordnung: Zuordnung[]; fehler: AsmFehler[] }

/** Übersetzt den Quelltext Zeile für Zeile über die Tabelle; Befehlszeilen landen in Zelle 0, 1, 2, … */
export function assemble(task: Task, quelltext: string): Assembliert {
  const sp = speicherStart(task);
  const datenzellen = new Set(task.daten.map(d => d.zelle));
  const zuordnung: Zuordnung[] = [], fehler: AsmFehler[] = [];
  let zelle = 0;
  const zeilen = quelltext.split("\n");
  for (let k = 0; k < zeilen.length; k++) {
    const n = k + 1;
    const teile = zeilen[k].split(";", 1)[0].split(/\s+/).filter(Boolean);
    if (!teile.length) continue;
    const fehl = (text: string) => fehler.push({ zeile: n, message: `Zeile ${n}: ${text}` });
    const name = teile[0].toUpperCase() as Befehl;
    const b = CODES[name] !== undefined ? BEFEHLE[CODES[name]] : undefined;
    if (!b) fehl(`unbekannter Befehl „${teile[0]}“`);
    else if (name !== "HALT" && !task.befehle.includes(name)) fehl(`${name} gibt es in dieser Aufgabe nicht`);
    else if (teile.length > 2) fehl("zu viel hinter dem Befehl");
    else if (b.adresse && teile.length === 1) fehl(`${name} braucht eine Adresse, z. B. ${name} 14`);
    else if (!b.adresse && teile.length === 2) fehl(`${name} hat keine Adresse`);
    else if (teile.length === 2 && !(/^[0-9]{1,2}$/.test(teile[1]) && Number(teile[1]) < ZELLEN)) {
      fehl(`Adresse „${teile[1]}“ gibt es nicht (0 bis 15, dezimal)`);
    }
    if (zelle >= ZELLEN || datenzellen.has(zelle)) {
      fehl(zelle < ZELLEN ? `Zelle ${zelle} enthält Daten -- das Programm ist zu lang`
        : "Der Speicher hat nur 16 Zellen -- das Programm ist zu lang");
      break;
    }
    if (!fehler.length || fehler[fehler.length - 1].zeile !== n) {
      const wort = CODES[name] + (teile.length === 2 ? adr(Number(teile[1])) : "0000");
      sp[zelle] = wort;
      zuordnung.push({ zeile: n, zelle, wort });
    }
    zelle++;
  }
  return { speicher: sp, zuordnung, fehler };
}

const leer = (problems: Problem[]): Result => ({ solved: false, problems, zustand: null, ereignisse: [], schritte: 0, par_erreicht: false });

/** Prüft eine Lösung (Maschinencode: {speicher}, Assembler: {quelltext}). */
export function check(task: Task, loesung: Loesung): Result {
  if (task.typ === "maschinencode") return checkProgramm(task, loesung as { speicher: string[] });
  const text = (loesung as { quelltext?: unknown })?.quelltext;
  if (typeof text !== "string" || text.length > MAX_QUELLTEXT) return leer([problem("ungueltig", null, "Ungültiger Quelltext")]);
  const asm = assemble(task, text);
  if (asm.fehler.length) return leer(asm.fehler.map(f => problem("assembler", String(f.zeile), f.message)));
  return checkProgramm(task, { speicher: asm.speicher });
}
